import { addDays } from './payPeriod';
import { displayLocale } from '../i18n/locale';
import type { WorkBucket } from '../types/nurseryWorkStats';

/** Longest range per grouping, in days. Mirrors HHNodeServer nurseryWorkStatsService. */
export const MAX_DAYS: Record<WorkBucket, number> = { hour: 7, day: 92, week: 371 };

export type RangePreset = 'today' | 'yesterday' | 'week' | 'last7' | 'last30' | 'last12w' | 'custom';

/** Days between two YYYY-MM-DD dates, both counted. */
export function spanDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;
}

/** The Monday on or before a date. */
export function mondayOf(date: string): string {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
  return addDays(date, -((dow + 6) % 7));
}

/** A preset's dates and the grouping that suits it. */
export function presetRange(preset: Exclude<RangePreset, 'custom'>, today: string): { from: string; to: string; bucket: WorkBucket } {
  switch (preset) {
    case 'today': return { from: today, to: today, bucket: 'hour' };
    case 'yesterday': { const d = addDays(today, -1); return { from: d, to: d, bucket: 'hour' }; }
    case 'week': return { from: mondayOf(today), to: today, bucket: 'day' };
    case 'last7': return { from: addDays(today, -6), to: today, bucket: 'day' };
    case 'last30': return { from: addDays(today, -29), to: today, bucket: 'day' };
    case 'last12w': return { from: mondayOf(addDays(today, -7 * 11)), to: today, bucket: 'week' };
  }
}

/** The finest grouping a range allows. */
export function fitBucket(from: string, to: string, wanted: WorkBucket): WorkBucket {
  const days = spanDays(from, to);
  const order: WorkBucket[] = ['hour', 'day', 'week'];
  for (const b of order.slice(order.indexOf(wanted))) if (days <= MAX_DAYS[b]) return b;
  return 'week';
}

function hourText(hour: number): string {
  return new Intl.DateTimeFormat(displayLocale(), { hour: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2000, 0, 1, hour)));
}

function dayText(date: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(displayLocale(), { ...options, timeZone: 'UTC' })
    .format(new Date(`${date}T12:00:00Z`));
}

/**
 * A bucket key as a short axis label. The keys are already the
 * organisation's local time, so they are formatted as UTC.
 */
export function bucketLabel(key: string, bucket: WorkBucket, multiDay = false): string {
  if (bucket === 'hour') {
    const h = hourText(Number(key.slice(11, 13)));
    return multiDay ? `${dayText(key.slice(0, 10), { weekday: 'short' })} ${h}` : h;
  }
  if (bucket === 'day') return dayText(key, { weekday: 'short', day: 'numeric', month: 'short' });
  return dayText(key, { day: 'numeric', month: 'short' });
}

/** An hour of the day (0–23) as "9 am". */
export function hourOfDayLabel(hour: number): string {
  return hourText(hour);
}

/** A local 'YYYY-MM-DDTHH:00' as "Tue 13 Oct, 9 am". */
export function hourKeyText(key: string): string {
  return `${dayText(key.slice(0, 10), { weekday: 'short', day: 'numeric', month: 'short' })}, ${hourText(Number(key.slice(11, 13)))}`;
}

export interface NamedSeries { key: string; label: string; series: number[]; total: number }

/**
 * The largest `keep` series by total, and the rest folded into one "others"
 * series, so a chart never needs more colours than it has.
 */
export function foldSeries(items: NamedSeries[], keep: number, othersLabel: string): NamedSeries[] {
  const sorted = [...items].sort((a, b) => b.total - a.total);
  if (sorted.length <= keep + 1) return sorted;
  const head = sorted.slice(0, keep);
  const rest = sorted.slice(keep);
  const len = rest[0].series.length;
  const series = new Array(len).fill(0);
  for (const r of rest) r.series.forEach((n, i) => { series[i] += n; });
  return [...head, { key: 'others', label: othersLabel, series, total: rest.reduce((s, r) => s + r.total, 0) }];
}

/** Percentage change, or null when there is nothing to compare with. */
export function changePct(now: number, before: number): number | null {
  if (!before) return null;
  return Math.round(((now - before) / before) * 100);
}
